import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import AiProductAssignments from "./AiProductAssignments";
import { GroupedAssignment, ProductOption } from "../measurementTypes";
import { StageMeasurementDraft } from "../stageMeasurements";
import { AssignmentSource } from "modules/aiMapping/aiAssignmentSources";
import type { AiAssignmentPersistence, AiSavedAssignment } from "./AiProductAssignments";
import { ProposalMeasurementType } from "generated/graphql";
import { useMemo } from "react";

type MeasurementsSidebarBodyProps = {
  measurements: StageMeasurementDraft[];
  selectedFeatureId: string | null;
  products: ProductOption[];
  groupedAssignments: GroupedAssignment[];
  aiMeasurementIds?: string[];
  aiAssignmentSources?: AssignmentSource[];
  additionalCosts?: { id: string; name: string }[];
  savedAiAssignments?: AiSavedAssignment[];
  persistence?: AiAssignmentPersistence;
  applyError: string | null;
  onSelectFeature: (featureId: string) => void;
  onUpdateProduct: (featureId: string, product: ProductOption | null) => void;
  onToggleScreenshot: (featureId: string, checked: boolean) => void;
};

export default function MeasurementsSidebarBody({
  measurements,
  selectedFeatureId,
  products,
  groupedAssignments,
  aiMeasurementIds = [],
  aiAssignmentSources = [],
  additionalCosts = [],
  savedAiAssignments = [],
  persistence,
  applyError,
  onSelectFeature,
  onUpdateProduct,
  onToggleScreenshot,
}: MeasurementsSidebarBodyProps) {
  const mapMeasurementSources = useMemo<AssignmentSource[]>(() => {
    const trackedAiIds = new Set(aiMeasurementIds);
    const isAiManaged = (measurement: StageMeasurementDraft): boolean => {
      if (trackedAiIds.has(measurement.featureId)) return true;
      try {
        return JSON.parse(measurement.featureGeojson)?.properties?.aiManaged === true;
      } catch {
        return false;
      }
    };

    return measurements
      .filter((measurement) => !isAiManaged(measurement))
      .map((measurement) => ({
        id: `map-measurement:${measurement.featureId}`,
        kind: "map_measurement",
        label: measurement.label,
        detail:
          measurement.measurementType === ProposalMeasurementType.AREA
            ? `${measurement.metrics.areaFeet.toLocaleString()} ft² / ${measurement.metrics.areaYards.toLocaleString()} yd²`
            : `${measurement.metrics.perimeterFeet.toLocaleString()} ft / ${measurement.metrics.perimeterYards.toLocaleString()} yd`,
        color: measurement.color ?? "#64748b",
        mapMeasurementId: measurement.featureId,
        assignedProductId: measurement.productId,
      }));
  }, [measurements, aiMeasurementIds]);
  const sources = useMemo(
    () => [...aiAssignmentSources, ...mapMeasurementSources],
    [aiAssignmentSources, mapMeasurementSources]
  );

  return (
    <MDBox px="20px" py="16px" sx={{ overflowY: "auto", flex: 1 }}>
      <AiProductAssignments
        sources={sources}
        products={products}
        additionalCosts={additionalCosts}
        savedAiAssignments={savedAiAssignments}
        persistence={persistence}
        onUpdateMapMeasurementProduct={onUpdateProduct}
      />

      {applyError && (
        <UiTypography uiVariant="body-sm" sx={{ color: "#D92D20", mt: "16px" }}>
          {applyError}
        </UiTypography>
      )}
    </MDBox>
  );
}
